import React from 'react';
import { QUOTE_STATUSES, DEPOSIT_STATUSES, isApprovedStatus, normalizeStatus, EXPIRY_STATES } from '../../utils/quoteSchema';

// r1-p7-partialbadge-8c3f9d24 — Phase 7.3. A partially-approved quote has
// status 'Approved' with some declined items; it's not a distinct status in
// the data model, so when isPartialApproval is true we DERIVE an amber
// "Partially Approved" badge (matching the amber partial language used in
// QuoteDetailsView and the Re-quote button). No new status is introduced.
//
// SEAM 6 VERIFIER: r1-dep-badge-compose-4f9b2c76
// Deposits Seam 6. Unlike partial approval, 'AwaitingDeposit' IS a real
// status — but the operator-facing state is still a COMPOSITE of two fields,
// status and depositStatus:
//   AwaitingDeposit          → "Awaiting Deposit"          (required deposit, signed, unpaid)
//   Approved + unpaid        → "Approved – deposit unpaid" (optional deposit, chased later)
//   Approved + paid/recorded → "Approved – deposit paid"
// Composing it here keeps every list, detail and portal surface consistent.
// depositStatus is OPTIONAL: an estimate with no deposit passes undefined and
// renders exactly as it did before this seam.
//
// CONVERT-FIX: r1-cvt-badge-isconverted-7d2e9f14
// Conversion to a job is NOT a status (Choice A: status stays Approved; the
// marker is quote.convertedToJobId, stamped by the onJobCreated trigger).
// isConverted is a DERIVED flag like isPartialApproval — internal surfaces
// pass !!quote.convertedToJobId. Checked FIRST: Converted is the terminal
// operator-facing state and wins over deposit composites. The customer
// portal deliberately does NOT pass it (defaults false), so customers keep
// seeing Approved/deposit states.
// SEAM 5 VERIFIER: qexp-6f2a9d38-badge
// Expiry is a DERIVED state, like isConverted above — quote.status stays 'Sent'
// and nothing writes 'Expired' anywhere. Callers pass the result of
// resolveExpiryState(quote, ...).state; the shared rule already returns 'none'
// for anything that isn't awaiting a customer decision, so an Approved or
// Draft estimate can never reach the expiry branches however stale its date.
// Default 'none' means every existing call site renders exactly as before.
//
// Expired is SLATE, not red: the estimate wasn't rejected, it just ran out of
// time. Red is Declined — the customer actually said no.
const QuoteStatusBadge = ({ status, isPartialApproval = false, depositStatus, isConverted = false, expiryState = EXPIRY_STATES.NONE }) => {
    const styles = {
        draft: 'bg-gray-100 text-gray-800',
        sent: 'bg-blue-100 text-blue-800',
        approved: 'bg-green-100 text-green-800',
        declined: 'bg-red-100 text-red-800',
        converted: 'bg-purple-100 text-purple-800',
    };
    const amber = 'bg-amber-100 text-amber-800';

    // The border is derived from the fill (text-→border-, 100→200) — existing
    // convention, factored into one helper now that there are more branches.
    const pill = (styleClass, label) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styleClass.replace('text-', 'border-').replace('100', '200')} ${styleClass}`}>
            {label}
        </span>
    );

    if (isConverted) {
        return pill(styles.converted, 'Converted');
    }

    // A required deposit holds the estimate out of Approved until it's paid.
    // Checked first: this state can't be partial, and it must never fall
    // through to the raw-status branch (which would print "AwaitingDeposit").
    if (normalizeStatus(status) === QUOTE_STATUSES.AWAITING_DEPOSIT) {
        return pill(amber, 'Awaiting Deposit');
    }

    // qexp-6f2a9d38-badge — placed AFTER Converted and AwaitingDeposit (both are
    // settled outcomes that outrank a date) and BEFORE the raw-status branch,
    // which would otherwise print a plain "Sent" on a dead estimate.
    if (expiryState === EXPIRY_STATES.EXPIRED) {
        return pill('bg-slate-100 text-slate-800', 'Expired');
    }
    if (expiryState === EXPIRY_STATES.EXPIRING_SOON) {
        return pill(amber, 'Expiring soon');
    }

    if (isPartialApproval) {
        return pill(amber, 'Partially Approved');
    }

    // Optional deposit — approved on signature, deposit tracked alongside.
    if (isApprovedStatus(status) && depositStatus) {
        if (depositStatus === DEPOSIT_STATUSES.PAID || depositStatus === DEPOSIT_STATUSES.RECORDED) {
            return pill(styles.approved, 'Approved – deposit paid');
        }
        if (depositStatus === DEPOSIT_STATUSES.REFUNDED) {
            return pill(styles.approved, 'Approved – deposit refunded');
        }
        // 1c-badge-3a7f1c9d — Seam 1c: an operator waived the deposit. Neutral
        // (slate) tone, and crucially NOT the "paid" treatment: no money changed
        // hands. Placed before the amber "unpaid" default.
        if (depositStatus === DEPOSIT_STATUSES.WAIVED) {
            return pill('bg-slate-100 text-slate-800', 'Approved – deposit waived');
        }
        return pill(amber, 'Approved – deposit unpaid');
    }

    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    const styleClass = styles[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';

    return pill(styleClass, label);
};

export default QuoteStatusBadge;
